import React, { useEffect, useState } from "react";
import emailjs from "@emailjs/browser";
import { motion } from "framer-motion";
import { FaEnvelope, FaPhone, FaWhatsapp, FaPaperPlane, FaCheckCircle } from "react-icons/fa";
import "./Contact.css";

const CONTACT_EMAIL = "contact@lankovate.com";
const CONTACT_PHONE = "+94766251456";
const WHATSAPP_URL = "https://wa.me/94766251456";
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const services = [
  "Odoo ERP",
  "Mobile app",
  "Website with SEO",
  "Online store",
  "POS or inventory",
  "Dashboards and reporting",
  "UI/UX design",
  "Other",
];

const budgets = ["Not sure yet", "Under LKR 100,000", "LKR 100,000 - 500,000", "LKR 500,000 - 1,000,000", "Over LKR 1,000,000"];

const empty = { name: "", email: "", phone: "", company: "", service: "", budget: "", message: "" };

const Contact = ({ defaultService = "" }) => {
  const [formData, setFormData] = useState({ ...empty, service: defaultService });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState("");

  useEffect(() => {
    const onPrefill = (e) => {
      setFormData((f) => ({ ...f, ...e.detail }));
      setIsSubmitted(false);
    };
    window.addEventListener("lk:prefill", onPrefill);
    return () => window.removeEventListener("lk:prefill", onPrefill);
  }, []);

  const handleInputChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSendError("");

    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      setSendError("Email service is not configured yet. Please email us directly.");
      return;
    }

    const templateParams = {
      to_email: CONTACT_EMAIL,
      subject: `New project inquiry from ${formData.name}`,
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone || "Not provided",
      company: formData.company || "Not provided",
      service: formData.service || "Not selected",
      budget: formData.budget || "Not selected",
      message: formData.message,
      reply_to: formData.email,
    };

    setIsSending(true);
    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);
      setIsSubmitted(true);
      setFormData(empty);
    } catch (error) {
      console.error("EmailJS send failed:", error);
      setSendError("Message could not be sent. Please email us directly.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section id="contact" className="lk-section">
      <div className="lk-container">
        <div className="lk-contact">
          <motion.div
            className="lk-contact-info"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="lk-eyebrow">Contact</div>
            <h2 className="lk-h2">Tell us what you want to build.</h2>
            <p className="lk-lead">
              Share a few details and we'll reply within one working day with
              next steps and a quote.
            </p>
            <ul className="lk-contact-list">
              <li><FaEnvelope aria-hidden="true" /><a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></li>
              <li><FaPhone aria-hidden="true" /><a href={`tel:${CONTACT_PHONE}`}>{CONTACT_PHONE}</a></li>
              <li><FaWhatsapp aria-hidden="true" /><a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">Chat on WhatsApp</a></li>
            </ul>
          </motion.div>

          <motion.div
            className="lk-card lk-form-card"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {isSubmitted ? (
              <div className="lk-sent" role="status">
                <FaCheckCircle aria-hidden="true" />
                <h3>Thank you!</h3>
                <p>We received your message and will be in touch soon.</p>
                <button type="button" className="lk-btn lk-btn-ghost" onClick={() => setIsSubmitted(false)}>
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="lk-form-row">
                  <label>Name *
                    <input name="name" value={formData.name} onChange={handleInputChange} required autoComplete="name" />
                  </label>
                  <label>Email *
                    <input type="email" name="email" value={formData.email} onChange={handleInputChange} required autoComplete="email" />
                  </label>
                </div>
                <div className="lk-form-row">
                  <label>Phone
                    <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} autoComplete="tel" />
                  </label>
                  <label>Company
                    <input name="company" value={formData.company} onChange={handleInputChange} autoComplete="organization" />
                  </label>
                </div>
                <div className="lk-form-row">
                  <label>I need
                    <select name="service" value={formData.service} onChange={handleInputChange}>
                      <option value="">Select a service</option>
                      {services.map((s) => (<option key={s} value={s}>{s}</option>))}
                    </select>
                  </label>
                  <label>Budget
                    <select name="budget" value={formData.budget} onChange={handleInputChange}>
                      <option value="">Select a range</option>
                      {budgets.map((b) => (<option key={b} value={b}>{b}</option>))}
                    </select>
                  </label>
                </div>
                <label>Project details *
                  <textarea name="message" rows="5" value={formData.message} onChange={handleInputChange} required />
                </label>
                {sendError && <p className="lk-form-error" role="alert">{sendError}</p>}
                <button type="submit" className="lk-btn lk-btn-primary lk-form-submit" disabled={isSending}>
                  {isSending ? "Sending..." : "Send message"}
                  <FaPaperPlane aria-hidden="true" />
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
